import { proxy } from 'valtio'

/**
 * Notifications Store - In-memory event store
 * 
 * Structure:
 * - events: Array of event objects
 * - maxEvents: Maximum number of events to keep in memory
 * - activeToasts: Currently visible notification toasts
 * - uploadToasts: Currently active upload toasts (separate from notifications)
 * - fatalModal: Current fatal error modal event
 */
export const notificationsStore = proxy({
    events: [],
    maxEvents: 1000,
    activeToasts: [], // Track currently visible toasts
    uploadToasts: [], // Track upload progress toasts { id, file, progress, status, error }
    fatalModal: null, // Current fatal error modal event
    confirmationToast: null // Current confirmation toast { id, message, onConfirm, onCancel }
})

/**
 * Add an event to the store
 * @param {Object} event - Event object with { id, timestamp, level, message, details? }
 */
export function addEvent(event) {
    notificationsStore.events.unshift(event) // Add to beginning (newest first)
    
    // Enforce max events limit
    if (notificationsStore.events.length > notificationsStore.maxEvents) {
        notificationsStore.events = notificationsStore.events.slice(0, notificationsStore.maxEvents)
    }
}

/**
 * Clear all events from the store
 */
export function clearEvents() {
    notificationsStore.events = []
}

/**
 * Get all events
 * @returns {Array} Array of events
 */
export function getEvents() {
    return notificationsStore.events
}

/**
 * Add a toast to active toasts
 * @param {string} eventId - ID of the event to show as toast
 */
export function addActiveToast(eventId) {
    if (!notificationsStore.activeToasts.includes(eventId)) {
        notificationsStore.activeToasts.push(eventId)
    }
}

/**
 * Remove a toast from active toasts
 * @param {string} eventId - ID of the event to remove
 */
export function removeActiveToast(eventId) {
    notificationsStore.activeToasts = notificationsStore.activeToasts.filter(id => id !== eventId)
}

/**
 * Clear all active toasts
 */
export function clearAllActiveToasts() {
    notificationsStore.activeToasts = []
}

/**
 * Set the fatal modal event
 * @param {Object|null} event - Event object or null to clear
 */
export function setFatalModal(event) {
    notificationsStore.fatalModal = event
}

// ============ Upload Toast Functions ============

/**
 * Add an upload toast
 * @param {Object} upload - Upload object { id, file, progress, status }
 * Note: file should be { name, type, size } metadata, not the actual File object
 */
export function addUploadToast(upload) {
    const existing = notificationsStore.uploadToasts.find(t => t.id === upload.id)
    if (!existing) {
        notificationsStore.uploadToasts.push({
            id: upload.id,
            file: upload.file, // Store only metadata: { name, type, size }
            progress: upload.progress || 0,
            status: upload.status || 'uploading',
            error: upload.error || null
        })
    }
}

/**
 * Update an upload toast's progress
 * @param {string} id - Upload toast ID
 * @param {number} progress - Progress (0-100)
 */
export function updateUploadProgress(id, progress) {
    const toast = notificationsStore.uploadToasts.find(t => t.id === id)
    if (toast) {
        toast.progress = progress
    }
}

/**
 * Update an upload toast's status
 * @param {string} id - Upload toast ID
 * @param {string} status - 'uploading' | 'success' | 'error'
 * @param {string} error - Optional error message
 */
export function updateUploadStatus(id, status, error = null) {
    const toast = notificationsStore.uploadToasts.find(t => t.id === id)
    if (toast) {
        toast.status = status
        toast.error = error
        if (status === 'success') {
            toast.progress = 100
        }
    }
}

/**
 * Remove an upload toast
 * @param {string} id - Upload toast ID
 */
export function removeUploadToast(id) {
    notificationsStore.uploadToasts = notificationsStore.uploadToasts.filter(t => t.id !== id)
}

/**
 * Clear all upload toasts
 */
export function clearAllUploadToasts() {
    notificationsStore.uploadToasts = []
}

// ============ Confirmation Toast Functions ============

/**
 * Set the confirmation toast
 * @param {Object} confirmation - Confirmation object { id, message, onConfirm, onCancel }
 */
export function setConfirmationToast(confirmation) {
    notificationsStore.confirmationToast = confirmation
}

/**
 * Clear the confirmation toast
 */
export function clearConfirmationToast() {
    notificationsStore.confirmationToast = null
}
